import type { MouseEvent, FC } from 'react';
import { Children, cloneElement, useEffect, useCallback, isValidElement } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import type { TFields } from '../form/models';
import { useFormSafe } from '../form/context';
import { getValidatorFromForm } from '../../helpers/get-validator-from-form';

import type { TAnalyticsControl } from './models';

/**
 * Компонент для разметки аналитикой формы.
 * Возможности компонента:
 *  * отправка события аналитики по клику на дочерний компонент — для таких кнопок, как «Попробовать снова»
 *  * отправка события при рендере — при возникновении одного из статусов: «ошибка» или «успех»
 *  * отправка события, если форма валидна — для таких кнопок, как «Перезвонить мне» и «Заполнить онлайн»
 */
export const AnalyticsControl: FC<TAnalyticsControl> = ({
    children = null,
    data,
    trigger = 'onClick',
    userIdFieldName,
    validate = false,
    ...restProps
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const formApi = useFormSafe<TFields>();
    const onTrigger = restProps[trigger];

    const [child] = Children.toArray(children);

    const handleSendEvent = () => {
        const userId = userIdFieldName ? formApi?.getState()?.values[userIdFieldName] : undefined;

        if (validate) {
            const isValid = getValidatorFromForm(formApi);

            if (!isValid()) return;
        }

        sendEvent(data);
        if (userIdFieldName && userId) {
            sendEvent({
                rko_userId: userId,
                ymEvent: { method: 'params', params: { rko_userId: userId } },
            });
        }
    };

    const handleTrigger = useCallback(
        (event: MouseEvent) => {
            if (typeof onTrigger === 'function') {
                onTrigger(event);
            }

            handleSendEvent();
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formApi, onTrigger]
    );

    useEffect(() => {
        if (!isValidElement(child)) {
            handleSendEvent();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formApi, child]);

    if (isValidElement(child)) {
        return cloneElement(child, {
            ...restProps,
            // @ts-ignore migration TS@4.4.4->4.9.5
            trigger,
            [trigger]: handleTrigger,
        });
    }

    return null;
};
